import { useEffect, useState } from "react";
import { Button, FormActions, TextareaField } from "@atlas/ui/web";

/**
 * Confirmacao de RECUSA de cadastro vindo do site institucional.
 *
 * Usado por /averbadora/prefeituras e /averbadora/bancos. Diferente de
 * "desativar" (reversivel, so troca status), recusar apaga o registro e tudo
 * que veio junto — por isso nao usa window.confirm: o operador precisa ler o
 * que vai embora e justificar. O motivo e' o unico rastro que sobra (vai pra
 * auditoria), entao e' obrigatorio.
 *
 * Nao reusa components/ConfirmDeleteModal.tsx: aquele e' um step-up por codigo
 * de e-mail (OTP), pesado demais pra uma acao de esteira do dia a dia.
 */
export function ConfirmarRecusaModal({
  titulo,
  alvo,
  protocolo,
  itensRemovidos,
  onConfirm,
  onClose,
}: {
  /** Cabecalho do modal. Ex.: "Recusar cadastro de prefeitura". */
  titulo: string;
  /** Identificacao do registro que sera apagado. Ex.: "Palhoça/SC — 12.345.678/0001-90". */
  alvo: string;
  /** Protocolo gerado pelo site institucional, quando existir. */
  protocolo?: string;
  /** O que vai junto no cascade — listado pro operador antes de confirmar. */
  itensRemovidos: string[];
  onConfirm: (motivo: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Esc fecha — mesma convencao do ConfirmDeleteModal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const motivoValido = motivo.trim().length >= 3;

  const confirmar = async () => {
    if (!motivoValido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await onConfirm(motivo.trim());
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err));
      setEnviando(false);
    }
  };

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={modalBackdrop}>
      <div onMouseDown={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0, color: "var(--danger-500)" }}>{titulo}</h3>

        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          <div style={{ color: "var(--text)", fontWeight: 600 }}>{alvo}</div>
          {protocolo ? (
            <div style={{ marginTop: 4 }}>
              Protocolo: <code style={{ fontFamily: "var(--font-mono)" }}>{protocolo}</code>
            </div>
          ) : null}
        </div>

        <div style={{
          padding: 14, borderRadius: 10,
          background: "color-mix(in srgb, var(--danger-500) 8%, transparent)",
          border: "1px solid color-mix(in srgb, var(--danger-500) 30%, transparent)",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--danger-500)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
            ⚠ Exclusão permanente e irreversível
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Recusar apaga o cadastro do banco de dados. Não é o mesmo que desativar —
            não há como reverter nem reativar depois. Serão removidos junto:
          </div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 13, color: "var(--text-muted)" }}>
            {itensRemovidos.map((i) => <li key={i}>{i}</li>)}
          </ul>
        </div>

        <TextareaField
          label="Motivo da recusa"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: CNPJ não corresponde ao órgão informado."
          rows={3}
          required
          maxLength={300}
          hint="Obrigatório, mínimo 3 caracteres. Fica registrado na auditoria — é o único rastro após a exclusão."
        />

        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}

        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>Voltar</Button>
          {/* Nao existe variante `danger` no Button — o padrao do repo e' ghost
              com as cores sobrescritas (ver ConfirmDeleteModal.tsx). */}
          <Button
            variant="ghost"
            type="button"
            disabled={!motivoValido || enviando}
            onClick={confirmar}
            style={{ borderColor: "var(--danger-500)", color: "var(--danger-500)" }}
          >
            {enviando ? "Recusando..." : "Recusar e excluir"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}

/** Desempacota details.fieldErrors / details.<campo> do envelope de erro da API. */
function mensagemDeErro(err: unknown): string {
  const e = err as { message?: string; details?: Record<string, unknown> };
  const det = e.details as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | undefined;
  const campos: string[] = [];
  if (det?.fieldErrors) {
    for (const [k, msgs] of Object.entries(det.fieldErrors)) {
      if (msgs?.length) campos.push(`${k}: ${msgs.join(", ")}`);
    }
  }
  if (det && !det.fieldErrors) {
    for (const [k, v] of Object.entries(det)) {
      if (typeof v === "string") campos.push(k === "status" ? v : `${k}: ${v}`);
    }
  }
  if (det?.formErrors?.length) campos.push(...det.formErrors);
  const base = e.message || "Erro ao recusar cadastro";
  return campos.length > 0 ? `${base} — ${campos.join(" | ")}` : base;
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
  zIndex: 110, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 520, width: "calc(100% - 32px)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  margin: "auto",
};
